// Shape of a tag / label-value option used across view JSONB columns
// (job grades/subjects, school admins, user schools).
export type Tag = { label: string; value: string };

type ListParams = {
  // columns to OR-match against `q` with ilike
  search?: string[];
  sort?: {
    // allowed sortBy keys mapped to one underlying column or an ordered list
    // (composite sorts apply the same direction to each column)
    map: Record<string, string | string[]>;
    // applied when no valid sortBy is provided (supports tiebreakers)
    default: Array<{ column: string; ascending?: boolean }>;
  };
};

// Reads `q`, `sortBy`, `sortDir` from URLSearchParams and applies them to a
// PostgREST query builder. Use for any list query (paged or not).
//   - q: independent text filter; ilike OR across opts.search columns
//   - sortBy: must be a key in opts.sort.map; otherwise ignored
//   - sortDir: only honored alongside a valid sortBy; defaults to asc
//   - default sort applies whenever no valid sortBy is given
export function applyListParams<
  Q extends {
    or: (...args: any[]) => any;
    order: (...args: any[]) => any;
  },
>(
  query: Q,
  params: URLSearchParams,
  opts: ListParams
): Q {
  const q = params.get("q")?.trim();
  if (q && opts.search?.length) {
    // Strip backslash + double-quote so we can safely wrap in PostgREST
    // quoted-value syntax (handles commas/parens in user input).
    const safe = q.replace(/[\\"]/g, "").trim();
    if (safe) {
      const filter = opts.search.map((c) => `${c}.ilike."%${safe}%"`).join(",");
      query = (query as any).or(filter);
    }
  }

  if (opts.sort) {
    const sortBy = params.get("sortBy");
    const mapped = sortBy ? opts.sort.map[sortBy] : null;
    if (mapped) {
      const ascending = params.get("sortDir") !== "desc";
      const cols = Array.isArray(mapped) ? mapped : [mapped];
      for (const col of cols) {
        query = (query as any).order(col, { ascending });
      }
    } else {
      for (const d of opts.sort.default) {
        query = (query as any).order(d.column, {
          ascending: d.ascending ?? true,
        });
      }
    }
  }

  return query;
}

// Promise.allSettled helpers: a rejected sub-query (or supabase error) nulls
// its fields and logs; other sources still resolve.
export function settledData<T>(
  r: PromiseSettledResult<any>,
  label: string
): { ok: true; data: T | null } | { ok: false } {
  if (r.status === "rejected") {
    console.error(`${label} failed:`, r.reason);
    return { ok: false };
  }
  if (r.value?.error) {
    console.error(`${label} error:`, r.value.error);
    return { ok: false };
  }
  return { ok: true, data: (r.value?.data ?? null) as T | null };
}

export function settledCount(
  r: PromiseSettledResult<any>,
  label: string
): { ok: true; count: number | null } | { ok: false } {
  if (r.status === "rejected") {
    console.error(`${label} failed:`, r.reason);
    return { ok: false };
  }
  if (r.value?.error) {
    console.error(`${label} error:`, r.value.error);
    return { ok: false };
  }
  return { ok: true, count: r.value?.count ?? null };
}
